import { engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Reel } from "../GameComponents/Reel";
import { Symbol } from "../GameComponents/Symbol";
import { Tweens } from "../Components/Effects/Tweens";
import { Settings } from "./Settings";
import { ReelConfigurationAttributes } from "../Components/Configuration";
import { SymbolExtended } from "./SymbolExtended";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { Symbols } from "../GameComponents/Symbols";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { WinAnimatorExtended } from "./WinAnimatorExtended";
import { PlayList } from "../Components/Sounds/PlayList";
import { ImageElement } from "../Components/Elements/Graphics/ImageElement";

/**
 * ReelExtended handles:
 * - z-order
 * - usage of Normal instead of Rotation
 */
@engineComponent(ComponentTypes.DisplayObject)
export class ReelExtended extends Reel {
    public static readonly SpinReelRollFinished: string = "spinReelRollFinished";
    public static readonly RespinReelStopped: string = "respinReelStopped";
    public static readonly RespinPreRollReelFinished: string = "respinPreRollReelFinished";
    public static readonly RespinRollReelFinished: string = "respinRollReelFinished";

    private static readonly TimerOffsetDelay: number = 2; // add delay for start timer
    private static readonly HorizontalNewSymbolsDelay: number = 23; // delay for symbols from data
    private static readonly RespinPreRollDuration: number = 12; // first symbols fall duration (symbols after the explosion)
    private static readonly RespinRollDuration: number = 12; // new symbols fall duration
    private static readonly RespinPreRollDurationForce: number = 6; // first symbols fall duration (symbols after the explosion)
    private static readonly RespinRollDurationForce: number = 12; // new symbols fall duration
    private static readonly SingleSymbolDelay: number = 3; // delay for single symbol
    private static readonly SingleSymbolHorizontalDelay: number = 2; // delay for single symbol in HORIZONTAL reel
    private static readonly DelayBetweenReel: number = 0; // DelayBetweenReel * delayMultiplyIndex - delay between reels
    private static readonly ImpactDelay: number = 25; // delay for impact animation after the symbols fall (symbols.xml Case="IMPACT")
    private static readonly PossibleSymbolsCountOnReel: Array<number> = [2, 3, 4, 5, 6];

    public static Tease: boolean = false;
    public static CanTeaseRespin: boolean = false;

    protected preRollHorizontal: number;
    protected preRollSpeedHorizontal: number;
    protected postRollHorizontal: number;
    protected postRollSpeedHorizontal: number;
    protected speedHorizontal: number;
    protected rollSpeedHorizontal: number;
    protected forceSpeedHorizontal: number;

    private easeFallSymbols: string = "sineOut";

    public delayMultiplyIndex: number = 0;

    public symbolsDataCount: number;
    public symbolSizeY: number;
    public symbolsTopCountOnReel: number = 0;
    public symbolsBottomCountOnReel: number = 0;
    public symbolRollDuration: number;
    public remainingSymbolCountInPackage: number = 0;

    protected horizontal: boolean = false;
    protected noTrimContainer: ContainerElement;
    protected noTrimTeasingContainer: ContainerElement;

    public currentSymbolsHorizontal: Array<Symbol> = [];
    public isStoppingHorizontal: boolean;

    public forced: boolean = false;
    public isRespin: boolean = false;
    public isCompleted: boolean = false;
    public isSkipped: boolean = false;

    public respinPreRollFinished: boolean = false;
    public respinRollFinished: boolean = false;
    public allSymbolsEmptyBeforeRespin: boolean = false;
    private hasData: boolean = false;
    private stoppingCounterTopSymbols: number = 0;
    private preRollFinishDelay: number = 0;
    private stoppingWasSkipped: boolean = false;
    public isRespinTease: boolean = false;
    private bottomLastSymbolId: number = -1;
    private isNewTeasingSymbol: boolean = false;
    private wasRespinPreRoll: boolean = false;
    private wasUpdateRespinReel: boolean = false;

    private spinCompleteTimer: FrameTimerEventEmitter;
    private respinCompleteTimer: FrameTimerEventEmitter;
    private respinPreRollFinishTimer: FrameTimerEventEmitter;
    private respinRollFinishTimer: FrameTimerEventEmitter;
    private horizontalReelStopSoundTimer: FrameTimerEventEmitter;

    public canTeasingSymbolHit: boolean = false;

    private hitSoundSymbolNumber: number;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        super();

        this.spinCompleteTimer = CreateFrameTimer("spinCompleteTimer");
        this.spinCompleteTimer.on(FrameTimerEvents.Complete, this.spinComplete.bind(this));

        this.respinPreRollFinishTimer = CreateFrameTimer("respinPreRollFinishTimer");
        this.respinPreRollFinishTimer.on(FrameTimerEvents.Complete, this.respinPreRollFinish.bind(this));

        this.respinRollFinishTimer = CreateFrameTimer("respinRollFinishTimer");
        this.respinRollFinishTimer.on(FrameTimerEvents.Complete, this.respinRollFinish.bind(this));

        this.respinCompleteTimer = CreateFrameTimer("respinCompleteTimer");
        this.respinCompleteTimer.on(FrameTimerEvents.Complete, this.respinComplete.bind(this));

        this.horizontalReelStopSoundTimer = CreateFrameTimer("horizontalReelStopSoundTimer");
        this.horizontalReelStopSoundTimer.on(
            FrameTimerEvents.Complete,
            this.playHorizontalSymbolImpactSound.bind(this),
        );
    }

    //******************************************************************************************************************
    //  CONFIGURE
    //******************************************************************************************************************

    public configure(node: Element): void {
        this.preRoll = node.hasAttribute(ReelConfigurationAttributes.PRE_ROLL)
            ? parseInt(node.getAttribute(ReelConfigurationAttributes.PRE_ROLL))
            : 180;
        this.preRollSpeed = node.hasAttribute(ReelConfigurationAttributes.PRE_ROLL_SPEED)
            ? parseInt(node.getAttribute(ReelConfigurationAttributes.PRE_ROLL_SPEED))
            : 10;
        this.postRoll = node.hasAttribute(ReelConfigurationAttributes.POST_ROLL)
            ? parseInt(node.getAttribute(ReelConfigurationAttributes.POST_ROLL))
            : 180;
        this.postRollSpeed = node.hasAttribute(ReelConfigurationAttributes.POST_ROLL_SPEED)
            ? parseInt(node.getAttribute(ReelConfigurationAttributes.POST_ROLL_SPEED))
            : 5;
        this.speed = node.hasAttribute(ReelConfigurationAttributes.SPEED)
            ? parseFloat(node.getAttribute(ReelConfigurationAttributes.SPEED))
            : 3;
        this.forceSpeed = node.hasAttribute(ReelConfigurationAttributes.FORCE_SPEED)
            ? parseFloat(node.getAttribute(ReelConfigurationAttributes.FORCE_SPEED))
            : 2;

        this.preRollHorizontal = node.hasAttribute(ReelConfigurationAttributes.PRE_ROLL_HORIZONTAL)
            ? parseInt(node.getAttribute(ReelConfigurationAttributes.PRE_ROLL_HORIZONTAL))
            : 180;
        this.preRollSpeedHorizontal = node.hasAttribute(ReelConfigurationAttributes.PRE_ROLL_SPEED_HORIZONTAL)
            ? parseInt(node.getAttribute(ReelConfigurationAttributes.PRE_ROLL_SPEED_HORIZONTAL))
            : 10;
        this.postRollHorizontal = node.hasAttribute(ReelConfigurationAttributes.POST_ROLL_HORIZONTAL)
            ? parseInt(node.getAttribute(ReelConfigurationAttributes.POST_ROLL_HORIZONTAL))
            : 180;
        this.postRollSpeedHorizontal = node.hasAttribute(ReelConfigurationAttributes.POST_ROLL_SPEED_HORIZONTAL)
            ? parseInt(node.getAttribute(ReelConfigurationAttributes.POST_ROLL_SPEED_HORIZONTAL))
            : 5;
        this.speedHorizontal = node.hasAttribute(ReelConfigurationAttributes.SPEED_HORIZONTAL)
            ? parseFloat(node.getAttribute(ReelConfigurationAttributes.SPEED_HORIZONTAL))
            : 3;
        this.forceSpeedHorizontal = node.hasAttribute(ReelConfigurationAttributes.FORCE_SPEED_HORIZONTAL)
            ? parseFloat(node.getAttribute(ReelConfigurationAttributes.FORCE_SPEED_HORIZONTAL))
            : 2;
    }

    //******************************************************************************************************************
    //  INITIALIZE
    //******************************************************************************************************************

    public initialize(
        previousReel: Reel,
        nextReel: Reel,
        index: number,
        symbols: Symbols,
        strip: Array<number>,
        positionX: number,
        positionY: number,
        symbolCount: number,
        symbolTopCount: number,
        symbolBottomCount: number,
        symbolWidth: number,
        symbolHeight: number,
        startDelay: number,
        stopDelay: number,
        winAnimator: WinAnimator,
        isHorizontal?: boolean,
        noTrimContainer?: ContainerElement,
        noTrimTeasingContainer?: ContainerElement,
    ): void {
        super.initialize(
            previousReel,
            nextReel,
            index,
            symbols,
            strip,
            positionX,
            positionY,
            symbolCount,
            symbolTopCount,
            symbolBottomCount,
            symbolWidth,
            symbolHeight,
            startDelay,
            stopDelay,
            winAnimator,
        );

        if (isHorizontal) {
            this.horizontal = isHorizontal;

            // HORIZONTAL has no symbols TOP and BOTTOM
            this.symbolTopCount = 0;
            this.symbolBottomCount = 0;
        } else {
            this.horizontal = false;
        }

        if (noTrimContainer) {
            this.noTrimContainer = noTrimContainer;
        }

        if (noTrimTeasingContainer) {
            this.noTrimTeasingContainer = noTrimTeasingContainer;
        }
    }

    public spinStart(): void {
        this.isRespin = false;
        this.isCompleted = false;
        this.isSkipped = false;
        this.forced = false;
        this.respinPreRollFinished = false;
        this.respinRollFinished = false;
        this.hasData = false;
        this.stoppingWasSkipped = false;
        this.isRespinTease = false;
        this.rollSpeedHorizontal = this.speedHorizontal;
        this.isStoppingHorizontal = false;
        this.isNewTeasingSymbol = false;
        this.wasRespinPreRoll = false;
        this.wasUpdateRespinReel = false;
        ReelExtended.CanTeaseRespin = false;

        super.spinStart();
    }

    public respinStart(delay?: number): void {
        this.isRespin = true;
        this.forced = false;
        this.isCompleted = false;
        this.isSkipped = false;
        this.respinPreRollFinished = false;
        this.respinRollFinished = false;
        this.hasData = false;
        this.stoppingWasSkipped = false;
        this.isRespinTease = false;
        this.shouldBeStopping = false;
        this.isStopping = false;
        this.stoppingCounter = 0;
        this.stoppingCounterTopSymbols = 0;
        this.isTeasing = false;
        this.teasingSymbols = [];
        this.rollSpeed = this.speed;
        this.rollSpeedHorizontal = this.speedHorizontal;
        this.stripIndex = (this.data.index - this.symbolTopCount + this.strip.length) % this.strip.length;
        this.preRollFinishDelay = 0;
        this.isStoppingHorizontal = false;
        this.isNewTeasingSymbol = false;
        this.wasRespinPreRoll = false;
        this.wasUpdateRespinReel = false;
        ReelExtended.CanTeaseRespin = false;

        if (delay != null) {
            this.delayMultiplyIndex = delay >= 0 ? delay : 0;
        }

        this.x = this.positionX;
        this.y = this.positionY;
    }

    protected respinPreRoll(): void {
        if (this.wasRespinPreRoll) {
            return;
        }

        this.wasRespinPreRoll = true;

        let count: number = 0;
        let maxDelay: number = 0;

        if (this.horizontal) {
            for (let i = 0; i < this.currentSymbols.length; i++) {
                let index = i; // this.currentSymbols.length - i - 1;
                let symbol = this.currentSymbols[i];

                if (symbol != null && symbol.getCurrentCase() != Symbol.CaseEmpty) {
                    this.currentSymbols[i].isNewFromData = false; // FALSE = previous symbols on reel

                    let emptySymbolCount = this.getEmptySymbolsBelow(index);

                    if (emptySymbolCount == 0) {
                        continue;
                    }

                    Tweens.removeTweens(symbol, "position");
                    Tweens.positionTween(symbol, {
                        tweenX: [symbol.x, symbol.x - this.symbolWidth * emptySymbolCount],
                        tweenY: [symbol.y, symbol.y],
                        tweenDelay:
                            ++count * ReelExtended.SingleSymbolDelay +
                            ReelExtended.DelayBetweenReel * this.delayMultiplyIndex, // !!! WARNING !!! ++count
                        tweenDuration: this.forced
                            ? ReelExtended.RespinPreRollDurationForce
                            : ReelExtended.RespinPreRollDuration,
                        tweenEase: this.easeFallSymbols,
                        callbackFunction: this.respinSymbolImpact,
                        callbackParams: [SymbolExtended.CaseImpact, false, symbol],
                        callbackScope: this,
                    });

                    // delay = tweenDelay + tweenDuration
                    let actualDelay: number =
                        count * ReelExtended.SingleSymbolDelay +
                        ReelExtended.DelayBetweenReel * this.delayMultiplyIndex +
                        (this.forced ? ReelExtended.RespinPreRollDurationForce : ReelExtended.RespinPreRollDuration);
                    if (actualDelay > maxDelay) {
                        maxDelay = actualDelay + ReelExtended.TimerOffsetDelay;
                        this.preRollFinishDelay =
                            maxDelay -
                            (this.forced
                                ? ReelExtended.RespinPreRollDurationForce
                                : ReelExtended.RespinPreRollDuration);
                    }
                }
            }
        } else {
            for (let i = 0; i < this.currentSymbols.length; i++) {
                let index = this.currentSymbols.length - i - 1;
                let symbol = this.currentSymbols[index];

                if (symbol != null && symbol.getCurrentCase() != Symbol.CaseEmpty) {
                    this.currentSymbols[i].isNewFromData = false; // FALSE = previous symbols on reel

                    let emptySymbolCount = this.getEmptySymbolsBelow(index);

                    if (emptySymbolCount == 0) {
                        continue;
                    }

                    Tweens.removeTweens(symbol, "position");
                    Tweens.positionTween(symbol, {
                        tweenX: [symbol.x, symbol.x],
                        tweenY: [symbol.y, symbol.y + this.symbolSizeY * emptySymbolCount],
                        tweenDelay:
                            ++count * ReelExtended.SingleSymbolDelay +
                            ReelExtended.DelayBetweenReel * this.delayMultiplyIndex, // !!! WARNING !!! ++count
                        tweenDuration: this.forced
                            ? ReelExtended.RespinPreRollDurationForce
                            : ReelExtended.RespinPreRollDuration,
                        tweenEase: this.easeFallSymbols,
                        callbackFunction: this.respinSymbolImpact,
                        callbackParams: [SymbolExtended.CaseImpact, false, symbol],
                        callbackScope: this,
                    });

                    // delay = tweenDelay + tweenDuration
                    let actualDelay: number =
                        count * ReelExtended.SingleSymbolDelay +
                        ReelExtended.DelayBetweenReel * this.delayMultiplyIndex +
                        (this.forced ? ReelExtended.RespinPreRollDurationForce : ReelExtended.RespinPreRollDuration);
                    if (actualDelay > maxDelay) {
                        maxDelay = actualDelay + ReelExtended.TimerOffsetDelay;
                        this.preRollFinishDelay =
                            maxDelay -
                            (this.forced
                                ? ReelExtended.RespinPreRollDurationForce
                                : ReelExtended.RespinPreRollDuration);
                    }
                }
            }
        }

        this.updateRespinSymbolsPositionInArray();

        this.respinPreRollFinishTimer.stop();
        this.respinPreRollFinishTimer.start(maxDelay); // without impact delay
    }

    protected respinPreRollFinish(): void {
        this.respinPreRollFinishTimer.stop();
        this.respinPreRollFinished = true;

        this.emit(ReelExtended.RespinPreRollReelFinished, this.index);
    }

    protected respinRollFinish(): void {
        this.respinRollFinishTimer.stop();
        this.respinRollFinished = true;

        if (this.isRespinTease) {
            this.hitSoundSymbolNumber = 0;

            for (let i = 0; i < this.currentSymbols.length; i++) {
                let symbol = this.currentSymbols[i];

                if (symbol != null) {
                    if (this.isNewTeasingSymbol == true) {
                        this.symbolTeasingTint(symbol, true);
                    } else {
                        if (symbol.isNewFromData == false) {
                            this.symbolTeasingTint(symbol, false);
                        }
                    }
                }
            }
        }

        this.emit(ReelExtended.RespinRollReelFinished, this.index);
    }

    protected respinSymbolImpact(newCase: string, isRoll: boolean, symbol: SymbolExtended): void {
        if (symbol && newCase) {
            // NO TRIM SELECTED SYMBOLS = move symbols to another notrim container (upper draw layer)
            if (Settings.SymbolsNoTrim.indexOf(symbol.id + 1) != -1) {
                if (ReelExtended.Tease && symbol.id + 1 != Settings.SymbolTeasing) {
                    // if teasing already on - skip
                    return;
                }

                if (this.horizontal) {
                    // HORIZONTAL
                    if (!(symbol.id + 1 == Settings.SymbolSpecial && !isRoll)) {
                        // special symbol for PreRoll (symbols on screen) is set in showRespinSymbols
                        symbol.x += this.positionX ? this.positionX : 0;
                        symbol.y += this.positionY ? this.positionY : 0;
                    }

                    if (isRoll) {
                        if (!ReelExtended.Tease) {
                            // if teasing already on - skip hit
                            newCase = SymbolExtended.CaseHit; // for new horizontal symbol no IMPACT but HIT
                            if (symbol.id + 1 == Settings.SymbolSpecial) {
                                this.playHitSound(symbol.id + 1, 0);
                            } else if (symbol.id + 1 == Settings.SymbolMystery) {
                                this.playHitSound(symbol.id + 1, 0);
                            }
                        }
                    }
                } else {
                    // OTHERS REELS
                    if (isRoll) {
                        // symbol for PreRoll (symbols on screen) is set in showRespinSymbols, for PostRoll (new symbols from data) must be set here
                        symbol.x += this.positionX ? this.positionX : 0;
                        symbol.y += this.positionY ? this.positionY : 0;
                    }
                }

                if (symbol.id + 1 == Settings.SymbolTeasing) {
                    if (isRoll) {
                        if (ReelExtended.CanTeaseRespin) {
                            newCase = SymbolExtended.CaseHit; // for new symbol teasing no IMPACT but HIT
                        }
                    }

                    if (this.noTrimTeasingContainer) {
                        this.noTrimTeasingContainer.addChild(symbol);
                    }
                } else {
                    if (this.noTrimContainer) {
                        this.noTrimContainer.addChild(symbol);
                    }
                }
            }

            // save symbol teasing reference to array
            if (this.winAnimator && symbol.id + 1 == Settings.SymbolTeasing) {
                // !!!! RESPIN CAN overwrite the previous symbol, respin has a higher priority
                (<WinAnimatorExtended>this.winAnimator).addTeasingSymbolsShine(
                    this.index,
                    <SymbolExtended>symbol,
                    true,
                );
            }

            if (symbol.symbolCountOnReel) {
                if (!this.horizontal && symbol.hasCase(newCase + "_" + symbol.symbolCountOnReel)) {
                    symbol.goToCase(newCase + "_" + symbol.symbolCountOnReel);
                } else if (this.horizontal && symbol.hasCase(newCase + "_HORIZONTAL")) {
                    symbol.goToCase(newCase + "_HORIZONTAL");
                } else if (symbol.hasCase(newCase)) {
                    symbol.goToCase(newCase);
                } else {
                    symbol.goToCase(SymbolExtended.CaseImpact); // DEFAULT is SymbolExtended.CaseImpact
                }
            } else {
                symbol.goToCase(SymbolExtended.CaseImpact); // DEFAULT is SymbolExtended.CaseImpact
            }
        }
    }

    private updateRespinSymbolsPositionInArray(): void {
        if (this.horizontal) {
            let counter = 0; //this.currentSymbols.length - 1;
            for (let i = 0; i < this.currentSymbols.length; i++) {
                if (this.currentSymbols[i] != null && this.currentSymbols[i].getCurrentCase() != Symbol.CaseEmpty) {
                    if (i != counter) {
                        let tempSymbol = this.currentSymbols[counter];
                        this.currentSymbols[counter] = this.currentSymbols[i];
                        this.currentSymbols[i] = tempSymbol;
                    }
                    counter++;
                }
            }
        } else {
            let counter = this.currentSymbols.length - 1;
            for (let i = this.currentSymbols.length - 1; i >= 0; i--) {
                if (this.currentSymbols[i] != null && this.currentSymbols[i].getCurrentCase() != Symbol.CaseEmpty) {
                    if (i != counter) {
                        let tempSymbol = this.currentSymbols[counter];
                        this.currentSymbols[counter] = this.currentSymbols[i];
                        this.currentSymbols[i] = tempSymbol;
                    }
                    counter--;
                }
            }
        }
    }

    public getEmptySymbolsBelow(index: number): number {
        let count = 0;

        if (this.horizontal) {
            for (let i = 0; i < index; i++) {
                let symbol = this.currentSymbols[i];
                if (symbol != null && symbol.getCurrentCase() == Symbol.CaseEmpty) {
                    count++;
                }
            }
        } else {
            for (let i = index; i < this.currentSymbols.length; i++) {
                let symbol = this.currentSymbols[i];
                if (symbol != null && symbol.getCurrentCase() == Symbol.CaseEmpty) {
                    count++;
                }
            }
        }

        return count;
    }

    public hasEmptySymbols(): boolean {
        if (this.currentSymbols && this.currentSymbols.length > 0) {
            for (let i = 0; i < this.currentSymbols.length; i++) {
                let symbol = this.currentSymbols[i];
                if (symbol != null && symbol.getCurrentCase() == Symbol.CaseEmpty) {
                    return true;
                }
            }
        }

        return false;
    }

    public spinStop(
        force: boolean = false,
        customDelay: number = 0,
        useCustomDelay: boolean = false,
        teasingDelay?: number,
        teasingCounter?: number,
    ): void {
        this.stopTimer.stop();

        if (!this.forced && force) {
            this.forced = force;
        }

        if (this.isSkipped) {
            this.isStopping = true;
            this.isStoppingHorizontal = true;
            this.respinComplete();
            return;
        }

        this.hasData = true;

        if (this.isRespin) {
            if (this.forced) {
                this.stopTimer.stop();
                if (!this.isCompleted) {
                    if (ReelExtended.Tease) {
                        if (!this.wasRespinPreRoll) {
                            this.respinPreRoll(); // fall symbols on screen
                        }

                        if (!this.wasUpdateRespinReel) {
                            teasingDelay = 10; // replace teasingDelay
                            this.stopTimer.start(teasingDelay); // call this.updateRespinReel() in timer

                            if (teasingDelay > 0 && teasingCounter != null) {
                                // delay "hack" for others reels after teasing previous reel
                                this.preRollFinishDelay =
                                    teasingCounter - 1 < 0 ? 0 : ReelExtended.RespinRollDurationForce;
                            }
                        }
                    } else {
                        this.respinCompleteTimer.stop();
                        this.returnAllSymbols();
                        this.respinComplete();
                    }
                }
            } else {
                this.respinPreRoll(); // fall symbols on screen
                if (teasingDelay != null) {
                    this.stopTimer.start(teasingDelay); // call this.updateRespinReel() in timer

                    if (teasingDelay > 0 && teasingCounter != null) {
                        // delay "hack" for others reels after teasing previous reel
                        this.preRollFinishDelay = teasingCounter - 1 < 0 ? 0 : ReelExtended.RespinRollDuration;
                    }
                } else {
                    this.updateRespinReel(); // new data symbols
                }
            }
        } else {
            if (this.forced) {
                this.rollSpeed = this.forceSpeed;
                this.rollSpeedHorizontal = this.forceSpeedHorizontal;
                // if(useCustomDelay) {
                //     this.stopTimer.start(customDelay);
                // } else {
                this.shouldBeStopping = true;
                this.stoppingWasSkipped = true;

                if (this.horizontal && !this.isStoppingHorizontal) {
                    // if forced before this.updateReelHorizontal()
                    this.updateReelHorizontal();
                }
                // }
            } else {
                this.stopTimer.start(customDelay !== 0 ? customDelay : this.stopDelay);
            }
        }
    }

    //******************************************************************************************************************
    //  SPIN PRE ROLL
    //******************************************************************************************************************

    protected spinPreRoll(): void {
        Tweens.removeTweens(this, "position");

        if (this.horizontal) {
            for (let i = 0; i < this.currentSymbols.length; i++) {
                let symbol = this.currentSymbols[i];

                if (symbol != null) {
                    Tweens.removeTweens(symbol, "position");
                    Tweens.positionTween(symbol, {
                        tweenX: [
                            symbol.x,
                            symbol.x + this.preRollHorizontal,
                            symbol.x - this.symbolWidth * this.data.symbols.length,
                        ],
                        tweenY: [symbol.y, symbol.y, symbol.y],
                        tweenDelay: this.startDelay,
                        tweenDuration: this.preRollSpeedHorizontal,
                        tweenEase: "sineIn",
                        callbackFunction: (): void => {
                            // on last cycle remove from currentSymbols because new symbols are in currentSymbolsHorizontal
                            if (i == this.currentSymbols.length - 1) {
                                while (this.currentSymbols.length > 0) {
                                    this.returnSymbol(this.currentSymbols.pop());
                                }
                            }
                        },
                        callbackScope: this,
                    });
                }
            }
        } else {
            Tweens.positionTween(this, {
                tweenX: [this.positionX, this.positionX, this.positionX],
                tweenY: [this.positionY, this.positionY - this.preRoll, this.positionY],
                tweenDuration: this.preRollSpeed,
                tweenDelay: this.startDelay,
                tweenEase: "sineIn",
                callbackFunction: this.spinRoll,
                callbackScope: this,
            });
        }
    }

    //******************************************************************************************************************
    //  SPIN ROLL
    //******************************************************************************************************************

    protected spinRoll(): void {
        Tweens.removeTweens(this, "position");

        if (this.horizontal) {
            // in HORIZONTAL reel no symbols ROLL, only preRoll (symbols on screen) and postRoll (new symbols from data)
        } else {
            Tweens.positionTween(this, {
                tweenX: [this.positionX, this.positionX],
                tweenY: [this.positionY, this.positionY + this.symbolSizeY],
                tweenDuration: this.symbolRollDuration ? this.symbolRollDuration : this.rollSpeed,
                callbackFunction: this.updateReel,
                callbackScope: this,
            });
        }
    }

    //******************************************************************************************************************
    //  SHOW SYMBOLS, SPIN ROLL
    //******************************************************************************************************************

    /**
     * Sorts symbols according to their zIndex attribute, if applicable
     */
    sortSymbols() {
        if (!this.symbols.hasZindex) {
            return;
        }
        let addQueue: Symbol[] = [];
        this.currentSymbols.forEach((symbol) => {
            addQueue.push(<Symbol>symbol);
        });
        addQueue.sort((a, b) => a.zIndex - b.zIndex);
        addQueue.forEach((symbol) => {
            this.addChild(symbol);
        });
    }

    public getRandomPossibleSymbolsCount(excludedSymbolsCount?: Array<number>): number {
        let symbolsCount: Array<number> = ReelExtended.PossibleSymbolsCountOnReel;

        if (excludedSymbolsCount && excludedSymbolsCount.length > 0) {
            symbolsCount = symbolsCount.filter((value) => excludedSymbolsCount.indexOf(value) == -1);
        }

        return symbolsCount[this.getRandom(0, symbolsCount.length - 1)];
    }

    public getSymbolSizeYFromCount(count: number): number {
        if (count && count > 0) {
            return (this.symbolCount * this.symbolHeight) / count;
        }

        return this.symbolHeight;
    }

    public getSpeedFromSymbolsCount(count: number): number {
        if (count && count > 0) {
            if (this.forced) {
                return (this.symbolCount / count) * this.forceSpeed;
            } else {
                return (this.symbolCount / count) * this.speed;
            }
        }

        return this.speed;
    }

    showSymbols(useTransform: boolean = false) {
        this.shouldBeStopping = false;
        this.isStopping = false;
        this.isStoppingHorizontal = false;
        this.stoppingCounter = 0;
        this.stoppingCounterTopSymbols = 0;
        this.isTeasing = false;
        this.teasingSymbols = [];
        this.forced = false;

        this.x = this.positionX;
        this.y = this.positionY;

        // if (!useTransform) {
        while (this.currentSymbols.length > 0) {
            this.returnSymbol(this.currentSymbols.pop());
        }

        while (this.currentSymbolsHorizontal.length > 0) {
            this.returnSymbol(this.currentSymbolsHorizontal.pop());
        }
        // }

        this.symbolsDataCount = this.data.symbols.length;

        if (!this.horizontal) {
            if (this.symbolsTopCountOnReel == 0) {
                this.symbolsTopCountOnReel = this.getRandomPossibleSymbolsCount([this.symbolsDataCount]);
            }

            if (this.symbolsBottomCountOnReel == 0) {
                this.symbolsBottomCountOnReel = this.getRandomPossibleSymbolsCount([
                    this.symbolsDataCount,
                    this.symbolsTopCountOnReel,
                ]);
            }

            // set speed and tween from TOP SYMBOLS because after this is call reel update (calculated by top symbols count)
            this.symbolRollDuration = this.getSpeedFromSymbolsCount(this.symbolsTopCountOnReel);
            this.symbolSizeY = this.getSymbolSizeYFromCount(this.symbolsTopCountOnReel);
            this.remainingSymbolCountInPackage =
                this.symbolsTopCountOnReel - this.symbolTopCount > 0
                    ? this.symbolsTopCountOnReel - this.symbolTopCount
                    : 0;
        } else {
            this.remainingSymbolCountInPackage = 0;
        }

        // get symbols data
        for (let i = 0; i < this.symbolBottomCount + this.symbolCount + this.symbolTopCount; i++) {
            let symbolId: number;
            let symbolCase: string = Symbol.CaseRotation;

            if (i < this.symbolTopCount) {
                // TOP SYMBOLS
                symbolId =
                    this.strip[(this.data.index - this.symbolTopCount + i + this.strip.length) % this.strip.length];
                symbolCase = Symbol.CaseBlur;
            } else if (i < this.symbolTopCount + this.symbolsDataCount) {
                // DATA SYMBOLS
                if (this.data.transform && this.data.transform[i - this.symbolTopCount] != -1) {
                    symbolId = this.data.transform[i - this.symbolTopCount];
                } else {
                    symbolId = this.data.symbols[i - this.symbolTopCount];
                }

                symbolCase = Symbol.CaseRotation;
            } else if (i < this.symbolTopCount + this.symbolsDataCount + this.symbolBottomCount) {
                // FIRST BOTTOM SYMBOL
                if (this.bottomLastSymbolId == -1) {
                    symbolId =
                        this.strip[(this.data.index - this.symbolTopCount + i + this.strip.length) % this.strip.length];
                } else {
                    symbolId = this.bottomLastSymbolId;
                }

                symbolCase = Symbol.CaseRotation;
            } else {
                // BOTTOM SYMBOLS
                symbolId =
                    this.strip[(this.data.index - this.symbolTopCount + i + this.strip.length) % this.strip.length];

                symbolCase = Symbol.CaseRotation;
            }

            if (this.horizontal) {
                symbolCase = Symbol.CaseNormal;
            }

            this.currentSymbols.push(this.getSymbol(symbolId - 1, symbolCase));
        }

        // set symbols positions
        let dataSymbolsOffsetY: number = 0;
        for (let i = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];

            let symbolCountCase: string;

            if (this.horizontal) {
                symbol.x = this.symbolWidth * (i - this.symbolTopCount) - symbol.offsetX;
                symbol.y = -symbol.offsetY;
                symbolCountCase = SymbolExtended.CaseCount + "_5";
                symbol.symbolCountOnReel = 5;
            } else {
                symbol.x = -symbol.offsetX;

                if (i < this.symbolTopCount) {
                    // TOP SYMBOLS
                    symbol.symbolSizeOnReelY = this.getSymbolSizeYFromCount(this.symbolsTopCountOnReel);
                    symbol.y = -symbol.symbolSizeOnReelY * (this.symbolTopCount - i) - symbol.offsetY;
                    symbolCountCase = SymbolExtended.CaseCount + "_" + this.symbolsTopCountOnReel;
                    symbol.symbolCountOnReel = this.symbolsTopCountOnReel;
                } else if (i < this.symbolTopCount + this.symbolsDataCount) {
                    // DATA SYMBOLS
                    symbol.symbolSizeOnReelY = this.getSymbolSizeYFromCount(this.symbolsDataCount);
                    symbol.y = symbol.symbolSizeOnReelY * (i - this.symbolTopCount) - symbol.offsetY;
                    symbolCountCase = SymbolExtended.CaseCount + "_" + this.symbolsDataCount;
                    symbol.symbolCountOnReel = this.symbolsDataCount;
                    dataSymbolsOffsetY += symbol.symbolSizeOnReelY;
                } else {
                    // BOTTOM SYMBOLS
                    symbol.symbolSizeOnReelY = this.getSymbolSizeYFromCount(this.symbolsBottomCountOnReel);
                    symbol.y =
                        symbol.symbolSizeOnReelY * (i - this.symbolTopCount - this.symbolsDataCount) -
                        symbol.offsetY +
                        dataSymbolsOffsetY;
                    symbolCountCase = SymbolExtended.CaseCount + "_" + this.symbolsBottomCountOnReel;
                    symbol.symbolCountOnReel = this.symbolsBottomCountOnReel;
                }
            }

            if (i >= this.symbolTopCount + this.symbolsDataCount + this.symbolBottomCount) {
                // HIDE INVISIBLE SYMBOLS = symbols that are not visible on the screen (below reels)
                symbol.goToCase(Symbol.CaseEmpty);
            } else {
                let symbolCase: string = symbol.getCurrentCase();
                symbol.goToCase(symbolCountCase); // for init size symbol, after this call basic CASE like NORMAL
                symbol.goToCase(symbolCase);
            }

            this.addChild(symbol);
        }

        // this.sortSymbols(); // probably not needed here :-)
    }

    showRespinSymbols(useTransform: boolean = false, currentSymbolsCases?: Array<string>) {
        this.shouldBeStopping = false;
        this.isStopping = false;
        this.isStoppingHorizontal = false;
        this.stoppingCounter = 0;
        this.stoppingCounterTopSymbols = 0;
        this.isTeasing = false;
        this.teasingSymbols = [];
        this.forced = false;

        this.x = this.positionX;
        this.y = this.positionY;

        // if (!useTransform) {
        while (this.currentSymbols.length > 0) {
            this.returnSymbol(this.currentSymbols.pop());
        }
        // }

        this.symbolsDataCount = this.data.symbols.length;
        this.symbolSizeY = this.getSymbolSizeYFromCount(this.symbolsDataCount);
        this.symbolRollDuration = this.getSpeedFromSymbolsCount(this.symbolsDataCount);

        let emptySymbolsCounter: number = 0;
        for (let i = 0; i < this.symbolsDataCount; i++) {
            let symbolId: number = this.data.symbols[i];
            if (useTransform && this.data.transform && this.data.transform[i] != -1) {
                symbolId = this.data.transform[i];
            }

            let symbolCase: string = currentSymbolsCases[i];

            if (symbolCase == Symbol.CaseEmpty) {
                emptySymbolsCounter++;
            }

            this.currentSymbols.push(this.getSymbol(symbolId - 1, symbolCase));
        }

        // check if ALL symbols are EMPTY
        if (emptySymbolsCounter == this.symbolsDataCount) {
            this.allSymbolsEmptyBeforeRespin = true;
        } else {
            this.allSymbolsEmptyBeforeRespin = false;
        }

        for (let i = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];

            let symbolCountCase: string;

            this.addChild(symbol);
            symbol.symbolSizeOnReelY = this.symbolSizeY;

            if (this.horizontal) {
                symbol.x = this.symbolWidth * i - symbol.offsetX;
                symbol.y = -symbol.offsetY;
                symbolCountCase = SymbolExtended.CaseCount + "_5";
                symbol.symbolCountOnReel = 5;
            } else {
                symbol.x = -symbol.offsetX;
                symbol.y = this.symbolSizeY * i - symbol.offsetY;
                symbolCountCase = SymbolExtended.CaseCount + "_" + this.symbolsDataCount;
                symbol.symbolCountOnReel = this.symbolsDataCount;
            }

            let symbolCase: string = symbol.getCurrentCase();
            symbol.goToCase(symbolCountCase); // for init size symbol, after this call basic CASE like NORMAL
            symbol.goToCase(symbolCase);

            // save symbol teasing reference to array
            if (symbol.id + 1 == Settings.SymbolTeasing) {
                // !!!! RESPIN CAN overwrite the previous symbol, respin has a higher priority
                (<WinAnimatorExtended>this.winAnimator).addTeasingSymbolsShine(
                    this.index,
                    <SymbolExtended>symbol,
                    true,
                );

                // move all teasing symbols on screen to notrim teasing container = pre roll symbols = symbol does not have to be in respin
                if (this.noTrimTeasingContainer) {
                    symbol.x += this.positionX ? this.positionX : 0;
                    symbol.y += this.positionY ? this.positionY : 0;

                    this.noTrimTeasingContainer.addChild(symbol);
                }
            } else if (symbol.id + 1 == Settings.SymbolSpecial) {
                symbol.x += this.positionX ? this.positionX : 0;
                symbol.y += this.positionY ? this.positionY : 0;

                this.noTrimContainer.addChild(symbol);
            }
        }

        // this.sortSymbols(); // probably not needed here :-)
    }

    protected onStopTimerComplete(): void {
        if (this.isRespin) {
            this.updateRespinReel(); // new data symbols
            // if(!this.isRespinTease) {
            PlayList.play("SND_BaseRespinfall0" + Settings.RespinNumber, 0, false);
            // }
        } else {
            this.shouldBeStopping = true;
            // this.isStopping = true;

            if (this.horizontal) {
                this.updateReelHorizontal();
            }
        }
    }

    protected spinComplete(): void {
        if (this.isCompleted) {
            return;
        }

        this.isCompleted = true;
        Tweens.removeTweens(this, "position");

        if (this.isStoppingHorizontal) {
            this.isStopping = true;
        }

        while (this.currentSymbols.length > 0) {
            let symbol = this.currentSymbols.pop();
            Tweens.removeTweens(symbol);
            this.returnSymbol(symbol);
        }

        while (this.currentSymbolsHorizontal.length > 0) {
            let symbol = this.currentSymbolsHorizontal.pop();
            Tweens.removeTweens(symbol);
            this.returnSymbol(symbol);
        }

        this.emit(Reel.ReelStopped, this.index);
    }

    protected respinComplete(): void {
        if (this.isCompleted) {
            return;
        }

        this.isCompleted = true;
        Tweens.removeTweens(this, "position");

        this.respinPreRollFinishTimer.stop();
        this.respinRollFinishTimer.stop();

        this.emit(ReelExtended.RespinReelStopped, this.index);
    }

    public returnAllSymbols(): void {
        while (this.currentSymbols.length > 0) {
            let symbol = this.currentSymbols.pop();
            Tweens.removeTweens(symbol);
            this.returnSymbol(symbol);
        }
    }

    protected updateReelHorizontal(): void {
        if (this.shouldBeStopping && !this.isStoppingHorizontal) {
            if (this.previousReel === null) {
                this.isStoppingHorizontal = true;
            } else if (this.previousReel.isStopping) {
                this.isStoppingHorizontal = true;
            }
        }

        if (this.isStoppingHorizontal) {
            this.stripIndex = (this.stripIndex - 1 + this.strip.length) % this.strip.length;

            let symbolId = this.strip[this.stripIndex];
            let symbolCase = Symbol.CaseNormal; // Symbol.CaseBlur;

            let dataSymbolsLength: number = this.data.symbols.length;

            for (let i = 0; i < dataSymbolsLength; i++) {
                symbolId = this.data.symbols[i];
                symbolCase = Symbol.CaseRotation;

                if (this.data.overlay && this.data.overlay[dataSymbolsLength - i - 1] != -1) {
                    symbolCase = Symbol.CaseEmpty;

                    if (this.data.transform && this.data.transform[dataSymbolsLength - i - 1] != -1) {
                        symbolCase = Symbol.CaseRotation;
                    }
                }

                const symbol = this.getSymbol(symbolId - 1, symbolCase);
                this.currentSymbolsHorizontal.push(symbol);

                // if (this.symbols.hasZindex) {
                //     let added = false;
                //     for (let index = 0; index < this.children.length; index++) {
                //         let child = <Symbol>this.children[index];
                //         if (child.zIndex >= symbol.zIndex) {
                //             this.addChildAt(symbol, index);
                //             added = true;
                //             break;
                //         }
                //     }
                //     if (!added) {
                //         this.addChild(symbol);
                //     }
                // } else {
                this.addChild(symbol);
                // }
            }

            for (let i = 0; i < this.currentSymbolsHorizontal.length; i++) {
                const symbolCurrent = this.currentSymbolsHorizontal[i];
                let symbolCountCase: string = "";

                symbolCurrent.x = this.symbolWidth * (i - this.symbolTopCount) - symbolCurrent.offsetX;
                symbolCurrent.y = -symbolCurrent.offsetY;
                symbolCountCase = SymbolExtended.CaseCount + "_5";
                symbolCurrent.symbolCountOnReel = 5;

                let symbolCase: string = symbolCurrent.getCurrentCase();
                symbolCurrent.goToCase(symbolCountCase); // for init size symbol, after this call basic CASE like NORMAL
                symbolCurrent.goToCase(symbolCase);
            }

            this.spinPostRoll();
        }
    }

    protected updateReel(): void {
        this.returnSymbol(this.currentSymbols.pop());

        if (this.shouldBeStopping && !this.isStopping && this.remainingSymbolCountInPackage == 0) {
            if (this.previousReel === null) {
                this.isStopping = true;
            } else if (this.previousReel.isStopping || (this.index == 1 && this.forced)) {
                // if reel 1 and force = ignore reel 0 (horizontal reel)
                if (this.stoppingWasSkipped) {
                    // skipping stopping for add next package of symbols (longer rotation) = reels not stopping at the same time side by side
                    this.isStopping = true;
                } else {
                    this.stoppingWasSkipped = true;
                }
            }
        }

        let isNewSymbolsPackage: boolean = false;

        if (this.remainingSymbolCountInPackage > 0) {
            this.remainingSymbolCountInPackage--;
        } else {
            if (this.isStopping) {
                // symbols package done and not need another
                this.remainingSymbolCountInPackage = 0;

                if (this.stoppingCounterTopSymbols == 0) {
                    // because top count is can be edited by stoppingCounterTopSymbols
                    this.symbolsBottomCountOnReel = this.symbolsTopCountOnReel; // set bottom symbol count for new spin (top symbols will be new bottom symbols for new spin)
                }
            } else {
                // random size set (count of symbols) new symbols package
                isNewSymbolsPackage = true;

                let excludedSymbolsCount: Array<number> = [];
                excludedSymbolsCount.push(this.symbolsTopCountOnReel); // exclude previous top, because is actually on screen

                if (this.hasData) {
                    excludedSymbolsCount.push(this.data.symbols.length); // exclude count from data symbols if has data
                }

                this.symbolsTopCountOnReel = this.getRandomPossibleSymbolsCount(excludedSymbolsCount);
                this.remainingSymbolCountInPackage = this.symbolsTopCountOnReel;
                this.remainingSymbolCountInPackage--;
                this.symbolsBottomCountOnReel = this.symbolsTopCountOnReel; // set bottom symbol count for new spin (top symbols will be new bottom symbols for new spin)
            }
        }

        this.stripIndex = (this.stripIndex - 1 + this.strip.length) % this.strip.length;

        let symbolId = this.strip[this.stripIndex];
        let symbolCase = Symbol.CaseBlur;

        if (this.isStopping) {
            let dataSymbolsLength: number = this.data.symbols.length;

            if (this.stoppingCounter < dataSymbolsLength) {
                symbolId = this.data.symbols[dataSymbolsLength - this.stoppingCounter - 1];
                symbolCase = Symbol.CaseRotation;

                if (this.data.overlay && this.data.overlay[dataSymbolsLength - this.stoppingCounter - 1] != -1) {
                    symbolCase = Symbol.CaseEmpty;

                    if (
                        this.data.transform &&
                        this.data.transform[dataSymbolsLength - this.stoppingCounter - 1] != -1
                    ) {
                        symbolCase = Symbol.CaseRotation;
                    }
                }

                if (this.stoppingCounter == 0) {
                    // save last bottom symbol for new spin
                    this.bottomLastSymbolId = this.strip[(this.stripIndex + 1 + this.strip.length) % this.strip.length];
                }
            } else {
                symbolId =
                    this.strip[
                        (this.data.index + dataSymbolsLength - this.stoppingCounter - 1 + this.strip.length) %
                            this.strip.length
                    ];
                symbolCase = this.stoppingCounter === dataSymbolsLength ? Symbol.CaseRotation : Symbol.CaseBlur;

                this.stoppingCounterTopSymbols++;
            }

            this.stoppingCounter++;
        }

        const symbol = this.getSymbol(symbolId - 1, symbolCase);
        this.currentSymbols.unshift(symbol);

        if (this.isStopping) {
            if (this.stoppingCounterTopSymbols > 0) {
                if (this.stoppingCounterTopSymbols == 1) {
                    // random TOP SYMBOLS over data symbols
                    this.symbolsTopCountOnReel = this.getRandomPossibleSymbolsCount([
                        this.data.symbols.length,
                        this.symbolsTopCountOnReel,
                    ]);
                }

                symbol.symbolSizeOnReelY = this.getSymbolSizeYFromCount(this.symbolsTopCountOnReel);
            } else {
                symbol.symbolSizeOnReelY = this.getSymbolSizeYFromCount(this.data.symbols.length);
            }
        }

        // if(this.symbols.hasZindex) {
        //     let added = false;
        //     for(let index = 0; index < this.children.length; index++) {
        //         let child = <Symbol>this.children[index];
        //         if(child.zIndex >= symbol.zIndex) {
        //             this.addChildAt(symbol, index);
        //             added = true;
        //             break;
        //         }
        //     }
        //     if(!added) {
        //         this.addChild(symbol);
        //     }
        // } else {
        this.addChild(symbol);
        // }

        let newPositionY: number = 0;
        for (let i = 0; i < this.currentSymbols.length; i++) {
            const symbolCurrent = this.currentSymbols[i];
            let symbolCountCase: string = "";
            symbolCurrent.x = -symbolCurrent.offsetX;
            let updateCountCase: boolean = true;

            if (i == 0) {
                // index 0 is new symbol
                if (this.isStopping) {
                    if (this.stoppingCounterTopSymbols > 0) {
                        // for TOP SYMBOLS
                        symbolCurrent.y = -symbolCurrent.symbolSizeOnReelY;
                        symbolCountCase = SymbolExtended.CaseCount + "_" + this.symbolsTopCountOnReel;
                        symbolCurrent.symbolCountOnReel = this.symbolsTopCountOnReel;
                    } else {
                        symbolCurrent.y = -symbolCurrent.symbolSizeOnReelY;
                        this.symbolSizeY = symbolCurrent.symbolSizeOnReelY;

                        this.symbolRollDuration = this.getSpeedFromSymbolsCount(this.data.symbols.length);
                        symbolCountCase = SymbolExtended.CaseCount + "_" + this.data.symbols.length;
                        symbolCurrent.symbolCountOnReel = this.data.symbols.length;
                    }
                } else {
                    if (isNewSymbolsPackage) {
                        isNewSymbolsPackage = false;
                        // set data for new symbolsTopCountOnReel (new random package of symbols)
                        let newY: number = this.getSymbolSizeYFromCount(this.symbolsTopCountOnReel);
                        symbolCurrent.y = -newY;
                        symbolCurrent.symbolSizeOnReelY = newY;
                        this.symbolSizeY = symbolCurrent.symbolSizeOnReelY;
                    } else {
                        // set data for new symbol from previous symbol (index+1)
                        symbolCurrent.y = -this.currentSymbols[i + 1].symbolSizeOnReelY;
                        symbolCurrent.symbolSizeOnReelY = this.currentSymbols[i + 1].symbolSizeOnReelY;
                    }

                    this.symbolRollDuration = this.getSpeedFromSymbolsCount(this.symbolsTopCountOnReel);
                    symbolCountCase = SymbolExtended.CaseCount + "_" + this.symbolsTopCountOnReel;
                    symbolCurrent.symbolCountOnReel = this.symbolsTopCountOnReel;
                }
            } else {
                symbolCurrent.y = newPositionY;
                newPositionY += this.currentSymbols[i].symbolSizeOnReelY;
                symbolCountCase = SymbolExtended.CaseCount + "_" + this.currentSymbols[i].symbolCountOnReel;
                updateCountCase = false; // skip COUNT case, because was set before (i = 0)
            }

            if (symbolCurrent.y >= (this.symbolCount + this.symbolBottomCount) * this.symbolHeight) {
                // HIDE INVISIBLE SYMBOLS = symbols that are not visible on the screen (below reels)
                symbolCurrent.goToCase(Symbol.CaseEmpty);
            } else {
                if (updateCountCase) {
                    // skip case if case was set already
                    let symbolCase: string = symbolCurrent.getCurrentCase();
                    symbolCurrent.goToCase(symbolCountCase); // for init size symbol, after this call basic CASE like NORMAL
                    symbolCurrent.goToCase(symbolCase);
                }
            }
        }

        if (this.isStopping && this.stoppingCounter === this.data.symbols.length + this.symbolTopCount) {
            this.emit(ReelExtended.SpinReelRollFinished, this.index); // roll finished, next is postRoll

            this.spinPostRoll();
        } else {
            this.spinRoll();
        }
    }

    protected updateRespinReel(): void {
        if (this.wasUpdateRespinReel) {
            return;
        }

        this.wasUpdateRespinReel = true;
        this.symbolsDataCount = this.data.symbols.length;

        for (let i = 0; i < this.symbolsDataCount; i++) {
            if (this.currentSymbols[i].getCurrentCase() != Symbol.CaseEmpty) {
                continue;
            }

            this.returnSymbol(this.currentSymbols[i]);
            this.currentSymbols[i] = null;
            this.currentSymbols[i] = this.getSymbol(this.data.symbols[i] - 1, SymbolExtended.CaseFalling); // this Case is check in respinRoll
            this.currentSymbols[i].symbolSizeOnReelY = this.getSymbolSizeYFromCount(this.symbolsDataCount);
            this.addChild(this.currentSymbols[i]);

            let symbolCountCase: string = "";

            if (this.horizontal) {
                this.currentSymbols[i].x = this.symbolWidth * i - this.currentSymbols[i].offsetX;
                this.currentSymbols[i].y = -this.currentSymbols[i].offsetY;
                symbolCountCase = SymbolExtended.CaseCount + "_5";
                this.currentSymbols[i].symbolCountOnReel = 5;
            } else {
                this.currentSymbols[i].x = -this.currentSymbols[i].offsetX;
                this.currentSymbols[i].y = this.currentSymbols[i].symbolSizeOnReelY * i;
                symbolCountCase = SymbolExtended.CaseCount + "_" + this.symbolsDataCount;
                this.currentSymbols[i].symbolCountOnReel = this.symbolsDataCount;
            }

            let symbolCase: string = this.currentSymbols[i].getCurrentCase();
            this.currentSymbols[i].goToCase(symbolCountCase); // for init size symbol, after this call basic CASE like NORMAL
            this.currentSymbols[i].goToCase(symbolCase);

            if (this.currentSymbols[i].id + 1 == Settings.SymbolTeasing) {
                this.isNewTeasingSymbol = true;
            }

            this.currentSymbols[i].isNewFromData = true; // TRUE = new falling symbols from data
        }

        this.respinRoll();
    }

    protected respinRoll(hardForce: boolean = false): void {
        if (this.isCompleted && hardForce) {
            return;
        }

        let count: number = 0;
        let maxDelay: number = 0;

        if (this.horizontal) {
            for (let i = 0; i < this.currentSymbols.length; i++) {
                let symbol = this.currentSymbols[i];

                if (symbol && symbol.getCurrentCase() == SymbolExtended.CaseFalling) {
                    Tweens.removeTweens(symbol, "position");
                    Tweens.positionTween(symbol, {
                        tweenX: hardForce
                            ? [symbol.x, this.symbolWidth * i]
                            : [this.symbolsDataCount * this.symbolWidth + this.symbolWidth * count++, symbol.x], // !!! WARNING !!! count++
                        tweenY: [symbol.y, symbol.y],
                        tweenDelay:
                            hardForce || this.forced
                                ? 0
                                : count * ReelExtended.SingleSymbolDelay +
                                  ReelExtended.DelayBetweenReel * this.delayMultiplyIndex +
                                  this.preRollFinishDelay,
                        tweenDuration: this.forced
                            ? ReelExtended.RespinRollDurationForce
                            : ReelExtended.RespinRollDuration,
                        tweenEase: this.easeFallSymbols,
                        callbackFunction: this.respinSymbolImpact,
                        callbackParams: [SymbolExtended.CaseImpact, true, symbol],
                        callbackScope: this,
                    });

                    // delay = tweenDelay + tweenDuration
                    let actualDelay: number =
                        (hardForce || this.forced
                            ? 0
                            : count * ReelExtended.SingleSymbolDelay +
                              ReelExtended.DelayBetweenReel * this.delayMultiplyIndex +
                              this.preRollFinishDelay) +
                        (this.forced ? ReelExtended.RespinRollDurationForce : ReelExtended.RespinRollDuration);
                    if (actualDelay > maxDelay) {
                        maxDelay = actualDelay + ReelExtended.TimerOffsetDelay;
                    }
                }
            }
        } else {
            // for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let i = this.currentSymbols.length - 1; i >= 0; i--) {
                let symbol = this.currentSymbols[i];

                if (symbol && symbol.getCurrentCase() == SymbolExtended.CaseFalling) {
                    Tweens.removeTweens(symbol, "position");
                    Tweens.positionTween(symbol, {
                        tweenX: [symbol.x, symbol.x],
                        tweenY: hardForce ? [symbol.y, this.symbolSizeY * i] : [-this.symbolSizeY * ++count, symbol.y], // !!! WARNING !!! ++count
                        tweenDelay:
                            hardForce || this.forced
                                ? 0
                                : count * ReelExtended.SingleSymbolDelay +
                                  ReelExtended.DelayBetweenReel * this.delayMultiplyIndex +
                                  this.preRollFinishDelay,
                        tweenDuration: this.forced
                            ? ReelExtended.RespinRollDurationForce
                            : ReelExtended.RespinRollDuration,
                        tweenEase: this.easeFallSymbols,
                        callbackFunction: this.respinSymbolImpact,
                        callbackParams: [SymbolExtended.CaseImpact, true, symbol],
                        callbackScope: this,
                    });

                    // delay = tweenDelay + tweenDuration
                    let actualDelay: number =
                        (hardForce || this.forced
                            ? 0
                            : count * ReelExtended.SingleSymbolDelay +
                              ReelExtended.DelayBetweenReel * this.delayMultiplyIndex +
                              this.preRollFinishDelay) +
                        (this.forced ? ReelExtended.RespinRollDurationForce : ReelExtended.RespinRollDuration);
                    if (actualDelay > maxDelay) {
                        maxDelay = actualDelay + ReelExtended.TimerOffsetDelay;
                    }
                }
            }
        }

        // timer without impact delay = all the symbols just fell out
        this.respinRollFinishTimer.stop();
        this.respinRollFinishTimer.start(maxDelay);

        // RESPIN ROLL DONE - complete timer with emit RespinReelStopped = symbols with impact delay
        this.respinCompleteTimer.stop();
        this.respinCompleteTimer.start(maxDelay + ReelExtended.ImpactDelay);
    }

    //******************************************************************************************************************
    //  SPIN POST ROLL
    //******************************************************************************************************************

    protected spinPostRoll(): void {
        Tweens.removeTweens(this, "position");

        this.hitSoundSymbolNumber = 0;

        if (this.horizontal) {
            let maxDelay: number = 0;

            for (let i = 0; i < this.currentSymbolsHorizontal.length; i++) {
                let symbol = this.currentSymbolsHorizontal[i];
                let hitDuration: number = 0;

                if (symbol != null) {
                    if (Settings.SymbolsHitDuration[symbol.id + 1] != null) {
                        hitDuration = Settings.SymbolsHitDuration[symbol.id + 1];
                    }

                    // this.hitSoundSymbolNumber = this.getPriorityHitSound(symbol.id + 1, this.hitSoundSymbolNumber);

                    Tweens.removeTweens(symbol, "position");
                    Tweens.positionTween(symbol, {
                        tweenX: [
                            symbol.x + this.symbolWidth * this.data.symbols.length,
                            symbol.x + this.postRollHorizontal,
                            symbol.x,
                        ],
                        tweenY: [symbol.y, symbol.y, symbol.y],
                        tweenDelay:
                            i * ReelExtended.SingleSymbolHorizontalDelay + ReelExtended.HorizontalNewSymbolsDelay,
                        tweenDuration: this.postRollSpeedHorizontal,
                        tweenEase: "sineOut",
                        callbackFunction: this.symbolHit,
                        callbackParams: [symbol],
                        callbackScope: this,
                    });

                    // delay = tweenDelay + tweenDuration + hitDuration
                    let actualDelay: number =
                        i * ReelExtended.SingleSymbolHorizontalDelay +
                        ReelExtended.HorizontalNewSymbolsDelay +
                        this.postRollSpeedHorizontal +
                        hitDuration;
                    if (actualDelay > maxDelay) {
                        maxDelay = actualDelay;
                    }
                }
            }

            this.hitSoundSymbolNumber = 0;
            this.horizontalReelStopSoundTimer.stop();
            this.horizontalReelStopSoundTimer.start(
                ReelExtended.HorizontalNewSymbolsDelay + this.postRollSpeedHorizontal - 14,
            );

            this.spinCompleteTimer.stop();
            this.spinCompleteTimer.start(maxDelay);
        } else {
            // set HIT case for symbols before spinPostRoll done
            let spinPostRollDelay: number = 0;
            let impactSound: number = 0;

            for (let i = 0; i < this.data.symbols.length; i++) {
                // check only data symbols
                const symbol = <SymbolExtended>this.currentSymbols[i + this.symbolTopCount]; // indexes: (0) top count + data count + bottom count + others (max)

                // check TEASING symbols HIT
                if (symbol.id + 1 == Settings.SymbolTeasing) {
                    if (this.canTeasingSymbolHit != null && this.canTeasingSymbolHit == false) {
                        continue;
                    }
                }

                this.hitSoundSymbolNumber = this.getPriorityHitSound(symbol.id + 1, this.hitSoundSymbolNumber);

                let delay: number = this.symbolHit(symbol);

                if (delay > 0) {
                    // NEEDED add same tween to symbol like spinPostRoll, because symbol is remove to other container
                    Tweens.positionTween(symbol, {
                        tweenX: [symbol.x, symbol.x, symbol.x],
                        tweenY: [symbol.y, symbol.y - this.postRoll, symbol.y],
                        tweenDuration: this.postRollSpeed,
                        tweenEase: "sineIn",
                    });
                }

                spinPostRollDelay = delay > spinPostRollDelay ? delay : spinPostRollDelay;
            }

            this.playHitSound(this.hitSoundSymbolNumber, this.index);

            // tween to whole reel
            Tweens.positionTween(this, {
                tweenX: [this.positionX, this.positionX, this.positionX],
                tweenY: [this.positionY, this.positionY - this.postRoll, this.positionY],
                tweenDuration: this.postRollSpeed,
                tweenEase: "sineIn",
                // callbackFunction: this.spinComplete,
                // callbackScope: this
            });

            this.spinCompleteTimer.stop();
            this.spinCompleteTimer.start(
                spinPostRollDelay > this.postRollSpeed ? spinPostRollDelay : this.postRollSpeed,
            );
        }
    }

    protected symbolHit(symbol: SymbolExtended): number {
        if (symbol) {
            if (ReelExtended.Tease && symbol.id + 1 != Settings.SymbolTeasing) {
                // if teasing already on - skip hit for others symbols
                return 0;
            }

            if (Settings.SymbolsHitDuration[symbol.id + 1] != null) {
                if (symbol.hasCase(SymbolExtended.CaseHit)) {
                    // NO TRIM SELECTED SYMBOLS = move symbols to another notrim container (upper draw layer)
                    symbol.x += this.positionX ? this.positionX : 0;
                    symbol.y += this.positionY ? this.positionY : 0;

                    if (symbol.id + 1 == Settings.SymbolTeasing) {
                        if (this.noTrimTeasingContainer) {
                            this.noTrimTeasingContainer.addChild(symbol);
                        }
                    } else {
                        if (this.noTrimContainer) {
                            this.noTrimContainer.addChild(symbol);
                        }
                    }

                    if (this.horizontal) {
                        if (symbol.id + 1 == Settings.SymbolSpecial) {
                            this.playHitSound(symbol.id + 1, 0);
                        } else if (symbol.id + 1 == Settings.SymbolMystery) {
                            this.playHitSound(symbol.id + 1, 0);
                        }
                    }

                    symbol.goToCase(SymbolExtended.CaseHit);
                }

                return Settings.SymbolsHitDuration[symbol.id + 1];
            }
        }

        return 0;
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    protected getSymbol(symbolId: number, symbolCase: string): Symbol {
        const symbol = super.getSymbol(symbolId, symbolCase);
        /*
        if (ReelExtended.Tease) {
            // if ((symbol.getCurrentCase() === Symbol.CaseBlur) && symbol.id !== Settings.SymbolTeasing) {
            if ((symbol.getCurrentCase() === Symbol.CaseBlur) || symbol.id !== Settings.SymbolTeasing) {
                if(this.teasingSymbols.length != 0) {   // teasing reel only which contains teasing symbol
                    symbol.goToCase(SymbolExtended.CaseTeasingActive);
                } else {
                    symbol.goToCase(SymbolExtended.CaseTeasingInactive);
                }

                symbol.alpha = 1;
            }
        } else {
            symbol.alpha = 1;
        }
*/
        return symbol;
    }

    public hasSymbol(symbolId: number): boolean {
        return this.data.symbols.indexOf(symbolId) !== -1;
    }

    public skipSpin(): void {
        this.isSkipped = true;
        this.isCompleted = false;
        this.respinPreRollFinished = true;
        this.respinRollFinished = true;
        this.allSymbolsEmptyBeforeRespin = false;
    }

    public getRandom(min: number, max: number): number {
        return Math.round(Math.random() * (max - min) + min);
    }

    public playHitSound(symbolNumber: number, reelIndex: number, onlyHighSymbols: boolean = false) {
        switch (symbolNumber) {
            case Settings.SymbolSpecial:
                // coin
                PlayList.play("SND_FSCoinHit", 0, false);
                break;
            case Settings.SymbolMystery:
                // mystery
                PlayList.play("SND_BaseMysteryHit" + reelIndex, 0, false);
                if (!this.horizontal) {
                    PlayList.play("SND_BaseRotationEnd" + reelIndex);
                }
                break;
            case Settings.SymbolTeasing:
                // bonus
                PlayList.play("SND_BaseScatter0" + (reelIndex - 1));
                break;
            default:
                if (!onlyHighSymbols) {
                    PlayList.play("SND_BaseRotationEnd" + reelIndex);
                }
        }
    }

    public getPriorityHitSound(symbolNumber: number, previousSymbolNumber: number): number {
        if (previousSymbolNumber == Settings.SymbolTeasing || symbolNumber == Settings.SymbolTeasing) {
            return Settings.SymbolTeasing;
        } else if (previousSymbolNumber == Settings.SymbolMystery || symbolNumber == Settings.SymbolMystery) {
            return Settings.SymbolMystery;
        } else if (previousSymbolNumber == Settings.SymbolSpecial || symbolNumber == Settings.SymbolSpecial) {
            return 0;
        } else {
            return symbolNumber;
        }
    }

    public respinTease(): void {
        if (this.isRespinTease) {
            for (let i = 0; i < this.currentSymbols.length; i++) {
                let symbol = this.currentSymbols[i];

                if (symbol != null && symbol.getCurrentCase() != Symbol.CaseEmpty) {
                    this.symbolTeasingTint(symbol, true); // black for symbols on screen
                }
            }
        }
    }

    public respinTeaseStop(): void {
        if (this.isRespinTease) {
            for (let i = 0; i < this.currentSymbols.length; i++) {
                // if(this.isNewTeasingSymbol == true) {
                //     this.symbolTeasingTint(this.currentSymbols[i], true);
                // } else {
                this.symbolTeasingTint(this.currentSymbols[i], false);
                // }
            }
        }
    }

    // for set TINT teasing symbol elements in code, NOT in CASE(xml), because tint can set during falling, impact or other animation case = NOT reset case
    private symbolTeasingTint(symbol: Symbol, black: boolean = false): void {
        if (symbol != null && symbol.id + 1 != Settings.SymbolTeasing) {
            let backgroundElement = symbol.getChildElementByName("Background");

            if (backgroundElement != null) {
                (<ImageElement>backgroundElement).tint = black ? 0x777777 : 0xffffff;
            }

            let symbolElement = symbol.getChildElementByName("Symbol");

            if (symbolElement != null) {
                (<ImageElement>symbolElement).tint = black ? 0x777777 : 0xffffff;
            }
        }
    }

    protected playHorizontalSymbolImpactSound(): void {
        this.playHitSound(this.hitSoundSymbolNumber, 0);
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/ReelExtended.ts
 **/